// src/components/DashboardAdmin/Dashboard/Dashboard.jsx  (adjust path if needed)
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { fetchICTData } from "../../../store/uiSlice";
import { fetchDomains } from "../../../store/domainsSlice";
import { fetchIctEnquiries } from "../../../store/ictEnquirySlice";
import { fetchFAQData } from "../../../store/faqSlice"; // keep FAQ

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import "../../../Styles/Common.css";

ChartJS.register(Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const RESULTS_PER_PAGE = 6;

function safeDateKey(value) {
  // returns YYYY-MM-DD or null
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const Dashboard = () => {
  const dispatch = useDispatch();

  // ICT
  const { ictData = [] } = useSelector((state) => state.ui || {});
  // Domains
  const { domains = [] } = useSelector((state) => state.domains || {});

  // Enquiries (Lead source)
  const {
    data: enquiries = [],
    loading: enqLoading,
    error: enqError,
  } = useSelector((state) => state.ictEnquiry || {});

  // FAQ
  const { faqData = [], faqLoading, faqError } = useSelector((state) => state.faq || {});

  // FAQ search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchICTData());
    dispatch(fetchDomains());
    dispatch(fetchIctEnquiries()); // NEW: enquiries
    dispatch(fetchFAQData());
  }, [dispatch]);

  // ------------------ BAR: Courses per Domain (VERTICAL) ------------------
  const { hasDomains, coursesPerDomainBarData, coursesPerDomainBarOptions, domainsChartHeightPx } =
    useMemo(() => {
      const domainCountsMap = ictData.reduce((acc, item) => {
        const key = item?.domainId;
        if (key === undefined || key === null) return acc;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      const stats = Object.entries(domainCountsMap)
        .map(([id, count]) => {
          const d = domains.find((dom) => dom?.id === Number(id));
          return { id: Number(id), count, label: d?.domain ? d.domain : `#${id}` };
        })
        .sort((a, b) => b.count - a.count);

      if (stats.length === 0) {
        return {
          hasDomains: false,
          coursesPerDomainBarData: { labels: [], datasets: [] },
          coursesPerDomainBarOptions: {},
          domainsChartHeightPx: 420,
        };
      }

      const dynamicHeight = Math.max(520, Math.min(1400, 260 + stats.length * 18));

      const data = {
        labels: stats.map((d) => d.label),
        datasets: [
          { label: "Courses per Domain", data: stats.map((d) => d.count), backgroundColor: "#36a2eb" },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } },
        scales: {
          y: { beginAtZero: true },
          x: {
            ticks: {
              autoSkip: false,
              maxRotation: 65,
              minRotation: 65,
              font: { size: 11 },
            },
          },
        },
      };

      return {
        hasDomains: true,
        coursesPerDomainBarData: data,
        coursesPerDomainBarOptions: options,
        domainsChartHeightPx: dynamicHeight,
      };
    }, [ictData, domains]);

  // ------------------ BAR: Courses per Type (VERTICAL) ------------------
  const coursesPerTypeBarData = useMemo(() => {
    const typeCounts = ictData.reduce((acc, item) => {
      const key = item?.ictType || "Unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(typeCounts),
      datasets: [
        {
          label: "Courses per Type",
          data: Object.values(typeCounts),
          backgroundColor: "#28a745",
        },
      ],
    };
  }, [ictData]);

  const coursesPerTypeBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
      scales: {
        y: { beginAtZero: true },
        x: { ticks: { autoSkip: false, maxRotation: 35, minRotation: 0, font: { size: 12 } } },
      },
    }),
    []
  );

  // ------------------ BAR: Enquiries per Date (VERTICAL) ------------------
  // Uses `created` field from enquiry records. If your API uses another field, change it below.
  const {
    hasEnquiries,
    enquiriesByDateBarData,
    enquiriesByDateBarOptions,
    enqDateChartHeightPx,
  } = useMemo(() => {
    if (!enquiries || enquiries.length === 0) {
      return {
        hasEnquiries: false,
        enquiriesByDateBarData: { labels: [], datasets: [] },
        enquiriesByDateBarOptions: {},
        enqDateChartHeightPx: 420,
      };
    }

    // Group by date (YYYY-MM-DD)
    const byDate = enquiries.reduce((acc, enq) => {
      const dateKey = safeDateKey(enq?.created); // IMPORTANT: uses created
      if (!dateKey) return acc;
      acc[dateKey] = (acc[dateKey] || 0) + 1;
      return acc;
    }, {});

    const stats = Object.entries(byDate)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date)); // chronological

    if (stats.length === 0) {
      return {
        hasEnquiries: false,
        enquiriesByDateBarData: { labels: [], datasets: [] },
        enquiriesByDateBarOptions: {},
        enqDateChartHeightPx: 420,
      };
    }

    // Height depends on number of days shown
    const dynamicHeight = Math.max(480, Math.min(1000, 260 + stats.length * 12));

    const data = {
      labels: stats.map((s) => s.date),
      datasets: [
        {
          label: "Enquiries per Date",
          data: stats.map((s) => s.count),
          backgroundColor: "#ffc107",
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
      scales: {
        y: { beginAtZero: true },
        x: {
          ticks: {
            autoSkip: true, // prevents overcrowding if many days
            maxRotation: 45,
            minRotation: 0,
            font: { size: 11 },
          },
        },
      },
    };

    return {
      hasEnquiries: true,
      enquiriesByDateBarData: data,
      enquiriesByDateBarOptions: options,
      enqDateChartHeightPx: dynamicHeight,
    };
  }, [enquiries]);

  // ------------------ FAQ Search ------------------
  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setSearchResults([]);
      setCurrentPage(1);
      return;
    }

    const results = faqData.filter((faq) => {
      const ques = (faq?.ques || "").toLowerCase();
      const ans = (faq?.ans || "").toLowerCase();
      const panelTitle = (faq?.panelTitle || "").toLowerCase();
      return ques.includes(q) || ans.includes(q) || panelTitle.includes(q);
    });

    setSearchResults(results);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setCurrentPage(1);
  };

  // Pagination for FAQ
  const indexOfLastResult = currentPage * RESULTS_PER_PAGE;
  const indexOfFirstResult = indexOfLastResult - RESULTS_PER_PAGE;
  const currentResults = searchResults.slice(indexOfFirstResult, indexOfLastResult);
  const totalPages = Math.ceil(searchResults.length / RESULTS_PER_PAGE);

  return (
    <div className="dashboard-container">
      {/* LEFT: CHARTS */}
      <div className="dashboard-left">
        <div className="charts">
          {/* Enquiries per Date */}
          <div className="chart">
            <h3>Enquiries by Date</h3>
            {enqLoading ? (
              <p className="loading">Loading enquiries...</p>
            ) : enqError ? (
              <p className="error">{enqError}</p>
            ) : !hasEnquiries ? (
              <p className="muted">No enquiry data available.</p>
            ) : (
              <div className="chart-canvas-wrap" style={{ height: `${enqDateChartHeightPx}px` }}>
                <Bar data={enquiriesByDateBarData} options={enquiriesByDateBarOptions} />
              </div>
            )}
          </div>

          <div className="circles">
            {/* Courses per Type */}
            <div className="chart">
              <h3>Courses per Type</h3>
              <div className="chart-canvas-wrap" style={{ height: "360px" }}>
                <Bar data={coursesPerTypeBarData} options={coursesPerTypeBarOptions} />
              </div>
            </div>

            {/* Courses per Domain */}
            <div className="chart domain-section">
              <h3>Courses per Domain</h3>
              {!hasDomains ? (
                <p className="muted">No domain data available.</p>
              ) : (
                <div className="chart-canvas-wrap domain-vertical-wrap" style={{ height: `${domainsChartHeightPx}px` }}>
                  <Bar data={coursesPerDomainBarData} options={coursesPerDomainBarOptions} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT: FAQ */}
      <div className="dashboard-right">
        <h3>FAQ Search</h3>

        <form onSubmit={handleSearch} className="faq-form">
          <input
            type="text"
            placeholder="Search FAQ (Ques, Ans, Panel Title)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="faqBtns">
            <button type="submit">
              <i className="fa fa-search"></i>
            </button>

            {searchResults.length > 0 && (
              <button type="button" onClick={handleClearSearch} className="cancel-btn">
                <i className="fa fa-close"></i>
              </button>
            )}
          </div>
        </form>

        {faqLoading ? (
          <p className="loading">Loading FAQs...</p>
        ) : faqError ? (
          <p className="error">{faqError}</p>
        ) : (
          <>
            {searchResults.length > 0 && (
              <p className="result-count">Total Results Found: {searchResults.length}</p>
            )}

            <div className="faq-tabs">
              {currentResults.map((faq, index) => (
                <div key={index} className="faq-tab">
                  <h4>{faq?.panelTitle}</h4>
                  <p>
                    <strong>Q:</strong> {faq?.ques}
                  </p>
                  <p>
                    <strong>A:</strong> {faq?.ans}
                  </p>
                </div>
              ))}
            </div>

            {searchResults.length > RESULTS_PER_PAGE && (
              <div className="pagination">
                {[...Array(totalPages).keys()].map((page) => (
                  <button
                    key={page + 1}
                    className={`pagination-btn ${currentPage === page + 1 ? "active" : ""}`}
                    onClick={() => setCurrentPage(page + 1)}
                    type="button"
                  >
                    {page + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
